import { Message } from '@crehana/compass.ui/v3/message';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Container } from '@crehana/ui';

import { PrereqBannerType } from '@/generated/graphql/types';

import useTeamCoursesTranslation from '../../hooks/useTeamCoursesTranslation';
import type { B2bTeamCoursesLocaleKeys } from '../../types';

export type NewCoursesBannerProps = {
  contentUpdatedAt: string | null;
  prereqsUpdatedAt: string | null;
  prereqBannerType: PrereqBannerType | null;
  isCompletedTrack: boolean;
};

// Copy variants agreed with product: one full sentence per banner_type, since
// each variant tells the user a different thing to do. PREREQ_REMOVED is not a
// variant of its own — it reuses the generic "prerequisites changed" message.
const PREREQ_DESCRIPTION_KEYS: Record<
  PrereqBannerType,
  B2bTeamCoursesLocaleKeys
> = {
  [PrereqBannerType.PrereqUpdated]:
    'LEARN_TEAM_TRACK_UPDATED_BANNER_PREREQ_UPDATED',
  [PrereqBannerType.PrereqRemoved]:
    'LEARN_TEAM_TRACK_UPDATED_BANNER_PREREQ_UPDATED',
  [PrereqBannerType.NewCoursePrereq]:
    'LEARN_TEAM_TRACK_UPDATED_BANNER_NEW_CONTENT_PREREQ',
  [PrereqBannerType.CourseRemoved]:
    'LEARN_TEAM_TRACK_UPDATED_BANNER_CONTENT_REMOVED',
};

export const NewCoursesBanner: React.FC<NewCoursesBannerProps> = ({
  contentUpdatedAt,
  prereqsUpdatedAt,
  prereqBannerType,
  isCompletedTrack,
}) => {
  const { t } = useTeamCoursesTranslation();

  // A prerequisite change is only actionable when the backend sends both the
  // timestamp and the variant to render.
  const prereqChangeType = prereqsUpdatedAt ? prereqBannerType : null;
  const hasContentUpdate = Boolean(contentUpdatedAt);
  const isVisible =
    (hasContentUpdate || Boolean(prereqChangeType)) && !isCompletedTrack;

  if (!isVisible) {
    return null;
  }

  // Variants that already mention the content change in their own sentence, so
  // adding the content line on top would repeat the same fact.
  const mentionsContentChange =
    prereqChangeType === PrereqBannerType.NewCoursePrereq ||
    prereqChangeType === PrereqBannerType.CourseRemoved;

  const descriptionKeys: B2bTeamCoursesLocaleKeys[] = [];
  if (hasContentUpdate && !mentionsContentChange) {
    descriptionKeys.push('LEARN_TEAM_TRACK_UPDATED_BANNER_NEW_CONTENT');
  }
  if (prereqChangeType) {
    descriptionKeys.push(PREREQ_DESCRIPTION_KEYS[prereqChangeType]);
  }

  return (
    <Container type="dashboard" className="tw-cds-mb-5">
      <Message
        variant="warning"
        title={t('LEARN_TEAM_NEW_COURSES_BANNER_TITLE')}
      >
        {descriptionKeys.length === 1 ? (
          t(descriptionKeys[0])
        ) : (
          // Message renders its children inside a <p>, so a real <ul> would be
          // invalid markup. The list roles give screen readers the same
          // semantics, and the markers come from CSS instead of a literal "•".
          <span
            role="list"
            className="tw-block tw-list-disc tw-cds-pl-5 tw-space-y-0.4"
          >
            {descriptionKeys.map(key => (
              <span key={key} role="listitem" className="tw-list-item">
                {t(key)}
              </span>
            ))}
          </span>
        )}
      </Message>
    </Container>
  );
};
